import { Box, Button, Flex } from '@ouster/webviz-react-components';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MergerAffectedUsersResponse,
  MergerDetailRecord,
  MergerStorageImpactResponse,
} from 'src/components/adminDashboard/types';
import useChakraHook from 'src/hooks/useChakraHook';
import { useStoreActions, useStoreState } from 'src/reducers';

import { STEP_LABELS } from './createWizard/constants';
import StepperHeader from './createWizard/StepperHeader';
import CommunicationStep from './createWizard/steps/CommunicationStep';
import StorageStep from './createWizard/steps/StorageStep';
import TopologyStep from './createWizard/steps/TopologyStep';
import UsersStep from './createWizard/steps/UsersStep';
import {
  CommunicationRecipientKey,
  OrgOption,
  StorageStats,
} from './createWizard/types';
import {
  buildStorageStatsFromImpact,
  computeInitialStorageOverride,
  getValidationError,
} from './createWizard/utils';

interface OrganizationMergeCreateWizardProps {
  onClose: () => void;
  onMergerSaved?: () => void | Promise<void>;
  mode?: 'create' | 'edit' | 'view';
  mergerDetails?: MergerDetailRecord | null;
}

export default function OrganizationMergeCreateWizard({
  onClose,
  onMergerSaved,
  mode = 'create',
  mergerDetails = null,
}: OrganizationMergeCreateWizardProps) {
  const getErrorDescription = (
    error: unknown,
    fallbackDescription: string
  ): string => {
    if (error instanceof Error && error.message) {
      return error.message;
    }

    return fallbackDescription;
  };

  const { showToast } = useChakraHook();
  const { fullOrgList } = useStoreState(
    (storeState) => storeState.organization
  );
  const { appUser } = useStoreState((storeState) => storeState.user);
  const { fetchFullOrgList } = useStoreActions(
    (actions) => actions.organization
  );
  const {
    getAffectedUsers,
    getStorageImpact,
    createMerger,
    updateMerger,
    executeMerger,
  } = useStoreActions((actions) => actions.adminDashboard);
  const { openConfirmDialog } = useStoreActions(
    (actions) => actions.confirmDialog
  );
  const isReadOnly = mode === 'view';
  const viewModeIndicatorLabel = useMemo(() => {
    if (!isReadOnly) {
      return '';
    }

    const status = mergerDetails?.status;

    if (!status) {
      return 'View Only';
    }

    if (status === 'PENDING') {
      return 'Pending Approval - View Only';
    }

    if (status === 'EXECUTED') {
      return 'Executed - Merge Completed';
    }

    if (status === 'DECLINED') {
      return 'Declined - Request Closed';
    }

    const normalizedStatus = status
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());

    return `${normalizedStatus} - View Only`;
  }, [isReadOnly, mergerDetails?.status]);

  const viewModeIndicatorStyles = useMemo(() => {
    const status = mergerDetails?.status;

    if (status === 'EXECUTED') {
      return {
        bg: 'green.800',
        color: 'green.100',
        borderColor: 'green.600',
      };
    }

    if (status === 'DECLINED') {
      return {
        bg: 'red.800',
        color: 'red.100',
        borderColor: 'red.600',
      };
    }

    return {
      bg: 'yellow.800',
      color: 'yellow.100',
      borderColor: 'yellow.600',
    };
  }, [mergerDetails?.status]);

  const [currentStep, setCurrentStep] = useState(0);
  const [survivorId, setSurvivorId] = useState<number | null>(null);
  const [sourceIds, setSourceIds] = useState<number[]>([]);
  const [storageStats, setStorageStats] = useState<StorageStats>({
    newStorageLimit: 0,
    survivorUsedGb: 0,
    incomingGb: 0,
    totalRequiredGb: 0,
    basePlanLimitGb: 0,
    usedPercent: 0,
    isReadOnlyRisk: false,
  });
  const [selectedRecipientKey, setSelectedRecipientKey] =
    useState<CommunicationRecipientKey | null>(null);
  const [sourceUserCountsByOrgId, setSourceUserCountsByOrgId] = useState<
    Record<number, number>
  >({});
  const [sourceStorageByOrgId, setSourceStorageByOrgId] = useState<
    Record<number, number>
  >({});
  const [selectedRecipientByOrgId, setSelectedRecipientByOrgId] = useState<
    Record<number, string>
  >({});
  const [isLoadingAffectedUsers, setIsLoadingAffectedUsers] = useState(false);
  const [
    affectedUsersResponseForCommunication,
    setAffectedUsersResponseForCommunication,
  ] = useState<MergerAffectedUsersResponse | null>(null);
  const storageStatsDraftRef = useRef<StorageStats | null>(null);

  const initialAffectedUsersResponse =
    useMemo<MergerAffectedUsersResponse | null>(() => {
      if (!mergerDetails) {
        return null;
      }

      const groupedUsersByOrg = mergerDetails.affected_users.reduce<
        Record<
          number,
          {
            org_name: string;
            users: {
              id: number;
              email: string;
              name: string;
              role: string;
            }[];
          }
        >
      >((acc, user) => {
        if (!acc[user.org_id]) {
          acc[user.org_id] = {
            org_name: user.org_name,
            users: [],
          };
        }

        acc[user.org_id].users.push({
          id: user.user_id,
          email: user.email,
          name: user.email,
          role: user.final_role,
        });

        return acc;
      }, {});

      const targetUsers =
        groupedUsersByOrg[mergerDetails.target_org_id]?.users ?? [];

      const sources = Object.entries(groupedUsersByOrg)
        .filter(([orgId]) => Number(orgId) !== mergerDetails.target_org_id)
        .map(([orgId, group]) => ({
          org_id: Number(orgId),
          org_name: group.org_name,
          users: group.users,
        }));

      return {
        target: {
          org_id: mergerDetails.target_org_id,
          org_name: mergerDetails.target_org_name,
          users: targetUsers,
        },
        sources,
      };
    }, [mergerDetails]);

  const initialStorageImpactResponse =
    useMemo<MergerStorageImpactResponse | null>(() => {
      if (!mergerDetails) {
        return null;
      }

      return {
        target: {
          storage_limit: mergerDetails.projected_storage.target_limit,
          storage_used: mergerDetails.projected_storage.target_used,
        },
        sources: mergerDetails.projected_storage.sources.map((source) => ({
          org_id: source.org_id,
          storage_limit: source.storage_limit,
          storage_used: source.storage_used,
        })),
      };
    }, [mergerDetails]);

  const savedRecipientsByOrgId = useMemo(() => {
    if (!mergerDetails) {
      return {};
    }

    const nextRecipientByOrgId: Record<number, string> = {};
    const maybeTargetRecipient = (
      mergerDetails as { target_recipient?: unknown }
    ).target_recipient;
    const targetRecipient =
      typeof maybeTargetRecipient === 'string' ? maybeTargetRecipient : null;

    if (typeof targetRecipient === 'string' && targetRecipient.length > 0) {
      nextRecipientByOrgId[mergerDetails.target_org_id] = targetRecipient;
    }

    const sourceRecipients = (mergerDetails as { source_recipients?: unknown })
      .source_recipients;
    if (Array.isArray(sourceRecipients)) {
      sourceRecipients.forEach((item) => {
        if (!item || typeof item !== 'object') {
          return;
        }

        const maybeOrgId = (item as { org_id?: unknown }).org_id;
        const maybeEmail = (item as { email?: unknown }).email;
        const orgId = Number(maybeOrgId);

        if (
          Number.isInteger(orgId) &&
          typeof maybeEmail === 'string' &&
          maybeEmail
        ) {
          nextRecipientByOrgId[orgId] = maybeEmail;
        }
      });
    }

    return nextRecipientByOrgId;
  }, [mergerDetails]);

  useEffect(() => {
    if (!mergerDetails) {
      return;
    }

    const incomingStorage = mergerDetails.projected_storage.sources.reduce(
      (sum, source) => sum + source.storage_used,
      0
    );
    const totalRequired =
      mergerDetails.projected_storage.target_used + incomingStorage;
    const basePlanLimit = Math.max(
      mergerDetails.projected_storage.target_limit,
      ...mergerDetails.projected_storage.sources.map(
        (source) => source.storage_limit
      )
    );
    const newStorageLimit = Math.max(
      Number(mergerDetails.projected_storage.new_storage_limit ?? 0),
      0
    );
    const denominator = newStorageLimit > 0 ? newStorageLimit : basePlanLimit;
    const usedPercent =
      denominator > 0 ? (totalRequired / denominator) * 100 : 0;

    // Seeds editable wizard state from mergerDetails (an async prop that
    // arrives/changes when the parent loads a merge record for edit/view
    // mode). The user then edits this state interactively across steps, so
    // it must be sticky and can't be recomputed purely from render.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentStep(0);
    setSurvivorId(mergerDetails.target_org_id);
    setSourceIds(mergerDetails.source_orgs);
    setSelectedRecipientKey(`survivor:${mergerDetails.target_org_id}`);
    setSelectedRecipientByOrgId(savedRecipientsByOrgId);

    setStorageStats({
      newStorageLimit,
      survivorUsedGb: mergerDetails.projected_storage.target_used,
      incomingGb: incomingStorage,
      totalRequiredGb: totalRequired,
      basePlanLimitGb: basePlanLimit,
      usedPercent,
      isReadOnlyRisk: totalRequired > denominator,
    });
  }, [mergerDetails, savedRecipientsByOrgId]);

  useEffect(() => {
    if (fullOrgList.length === 0) {
      fetchFullOrgList();
    }
  }, [fetchFullOrgList, fullOrgList.length]);

  const orgOptions = useMemo<OrgOption[]>(
    () =>
      fullOrgList.map((org) => ({
        label: `${org.org_name} (${org.creator_email})`,
        value: org.org_id.toString(),
        data: org,
      })),
    [fullOrgList]
  );

  const topologyOrgOptions = useMemo<OrgOption[]>(
    () =>
      isReadOnly
        ? orgOptions
        : orgOptions.filter((org) => org.data.status === 'ACTIVE'),
    [isReadOnly, orgOptions]
  );

  const survivor = useMemo(
    () =>
      survivorId
        ? orgOptions.find((org) => org.data.org_id === survivorId) ?? null
        : null,
    [orgOptions, survivorId]
  );

  const sources = useMemo(
    () =>
      sourceIds
        .map((sourceId) =>
          orgOptions.find((org) => org.data.org_id === sourceId)
        )
        .filter((org): org is OrgOption => Boolean(org)),
    [orgOptions, sourceIds]
  );

  const topologyError = useMemo(
    () => getValidationError(survivor, sources),
    [survivor, sources]
  );

  const isNextDisabled =
    !isReadOnly && currentStep === 0 && Boolean(topologyError);

  const isFinalStep = currentStep === STEP_LABELS.length - 1;
  const mergerStatus = mergerDetails?.status ?? 'DRAFT';
  const isPendingMerger = mergerStatus === 'PENDING';
  const isClosedMerger =
    mergerStatus === 'DECLINED' || mergerStatus === 'EXECUTED';
  const requiredApprovalCount =
    sourceIds.length > 0
      ? sourceIds.length + 1
      : mergerDetails
      ? mergerDetails.source_orgs.length + 1
      : 0;

  const approvedSourceCount = useMemo(() => {
    const sourceApprovals = mergerDetails?.source_approvals;

    if (!sourceApprovals) {
      return 0;
    }

    return sourceApprovals.filter((sourceApproval) => sourceApproval.approved)
      .length;
  }, [mergerDetails?.source_approvals]);

  const hasAllRequiredApprovals =
    isPendingMerger &&
    requiredApprovalCount > 0 &&
    (mergerDetails?.target_approval ? 1 : 0) + approvedSourceCount ===
      requiredApprovalCount;
  const shouldShowFinalActionButton = !isClosedMerger;
  const isFinalActionDisabled = isPendingMerger && !hasAllRequiredApprovals;
  const finalActionLabel = isPendingMerger
    ? 'Merge Request'
    : 'Create Merge Request';

  const hasSameTopologyAsMergerDetails = useMemo(() => {
    if (!mergerDetails || survivorId === null) {
      return false;
    }

    if (survivorId !== mergerDetails.target_org_id) {
      return false;
    }

    const currentSources = [...sourceIds].sort((a, b) => a - b);
    const mergerSources = [...mergerDetails.source_orgs].sort((a, b) => a - b);

    if (currentSources.length !== mergerSources.length) {
      return false;
    }

    return currentSources.every(
      (sourceId, index) => sourceId === mergerSources[index]
    );
  }, [mergerDetails, sourceIds, survivorId]);

  const hasInitialAffectedUsers = useMemo(() => {
    if (!initialAffectedUsersResponse) {
      return false;
    }

    const targetCount = initialAffectedUsersResponse.target.users.length;
    const sourceCount = initialAffectedUsersResponse.sources.reduce(
      (sum, source) => sum + source.users.length,
      0
    );

    return targetCount + sourceCount > 0;
  }, [initialAffectedUsersResponse]);

  const hasUsableInitialAffectedUsers = useMemo(() => {
    if (
      !initialAffectedUsersResponse ||
      survivorId === null ||
      sourceIds.length === 0
    ) {
      return false;
    }

    const initialTargetOrgId = Number(
      initialAffectedUsersResponse.target.org_id
    );
    if (initialTargetOrgId !== survivorId) {
      return false;
    }

    const initialSourceOrgIds = new Set(
      initialAffectedUsersResponse.sources
        .map((source) => Number(source.org_id))
        .filter((orgId) => Number.isInteger(orgId))
    );

    return sourceIds.every((sourceId) => initialSourceOrgIds.has(sourceId));
  }, [initialAffectedUsersResponse, sourceIds, survivorId]);

  const hasInitialOriginalRoleData = useMemo(() => {
    if (!initialAffectedUsersResponse) {
      return false;
    }

    const allUsers = [
      ...initialAffectedUsersResponse.target.users,
      ...initialAffectedUsersResponse.sources.flatMap((source) => source.users),
    ];

    return allUsers.some(
      (user) =>
        typeof user.original_user_role === 'string' &&
        user.original_user_role.trim().length > 0
    );
  }, [initialAffectedUsersResponse]);

  useEffect(() => {
    if (!isFinalStep || !survivor) {
      return;
    }

    const survivorKey = `survivor:${survivor.data.org_id}` as const;
    const sourceKeys = new Set(
      sources.map(
        (source) => `source:${source.data.org_id}` as CommunicationRecipientKey
      )
    );

    const hasValidSelection =
      selectedRecipientKey === survivorKey ||
      (selectedRecipientKey ? sourceKeys.has(selectedRecipientKey) : false);

    if (!hasValidSelection) {
      // selectedRecipientKey is sticky user-selection state that must
      // persist across renders; this only resets it to the survivor default
      // when the topology changes and the previous selection is no longer
      // one of the valid options, so it can't be derived from render alone.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedRecipientKey(survivorKey);
    }
  }, [isFinalStep, selectedRecipientKey, sources, survivor]);

  useEffect(() => {
    if (survivorId === null) {
      // selectedRecipientByOrgId accumulates sticky per-org selections made
      // by the user across renders; this effect only prunes/reacts to
      // topology (survivorId/sourceIds) changes, so it can't be derived
      // fresh from render alone.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedRecipientByOrgId({});
      return;
    }

    const validOrgIds = new Set([survivorId, ...sourceIds]);
    setSelectedRecipientByOrgId((prev) => {
      const next: Record<number, string> = {};

      Object.entries(prev).forEach(([orgId, email]) => {
        const numericOrgId = Number(orgId);
        if (validOrgIds.has(numericOrgId)) {
          next[numericOrgId] = email;
        }
      });

      return next;
    });
  }, [sourceIds, survivorId]);

  useEffect(() => {
    if (survivorId === null || sourceIds.length === 0) {
      // This effect orchestrates async loading of affected-users/storage
      // impact data (via getAffectedUsers/getStorageImpact below) whenever
      // the topology changes; this branch just resets that derived state
      // before/instead of triggering a fetch, which is inherently tied to
      // the async data source rather than derivable from render.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSourceUserCountsByOrgId({});
      setSourceStorageByOrgId({});
      setAffectedUsersResponseForCommunication(null);
      setIsLoadingAffectedUsers(false);
      return;
    }

    if (
      hasSameTopologyAsMergerDetails &&
      hasInitialAffectedUsers &&
      hasUsableInitialAffectedUsers &&
      hasInitialOriginalRoleData &&
      initialAffectedUsersResponse &&
      initialStorageImpactResponse
    ) {
      const nextCounts = initialAffectedUsersResponse.sources.reduce<
        Record<number, number>
      >((acc, source) => {
        const orgId = Number(source.org_id);
        acc[orgId] = source.users.length;
        return acc;
      }, {});
      const nextStorage = initialStorageImpactResponse.sources.reduce<
        Record<number, number>
      >((acc, source) => {
        acc[source.org_id] = source.storage_used;
        return acc;
      }, {});

      setSourceUserCountsByOrgId(nextCounts);
      setSourceStorageByOrgId(nextStorage);
      setAffectedUsersResponseForCommunication(initialAffectedUsersResponse);
      setIsLoadingAffectedUsers(false);
      setStorageStats((prevStats) => {
        const resolvedStorageLimit =
          prevStats.newStorageLimit > 0
            ? prevStats.newStorageLimit
            : computeInitialStorageOverride(initialStorageImpactResponse);

        return buildStorageStatsFromImpact(
          initialStorageImpactResponse,
          resolvedStorageLimit
        );
      });
      return;
    }

    setIsLoadingAffectedUsers(true);
    getAffectedUsers({
      target_org: survivorId,
      source_orgs: sourceIds.join(','),
    })
      .then((response: MergerAffectedUsersResponse) => {
        const nextCounts = response.sources.reduce<Record<number, number>>(
          (acc, source) => {
            const orgId = Number(source.org_id);
            acc[orgId] = source.users.length;
            return acc;
          },
          {}
        );
        setSourceUserCountsByOrgId(nextCounts);
        setAffectedUsersResponseForCommunication(response);
      })
      .catch(() => {
        setSourceUserCountsByOrgId({});
        setAffectedUsersResponseForCommunication(null);
      })
      .finally(() => {
        setIsLoadingAffectedUsers(false);
      });

    getStorageImpact({
      target_org: survivorId,
      source_orgs: sourceIds.join(','),
    })
      .then((response: MergerStorageImpactResponse) => {
        const nextStorage = response.sources.reduce<Record<number, number>>(
          (acc, source) => {
            acc[source.org_id] = source.storage_used;
            return acc;
          },
          {}
        );
        setSourceStorageByOrgId(nextStorage);
        setStorageStats((prevStats) => {
          const resolvedStorageLimit =
            prevStats.newStorageLimit > 0
              ? prevStats.newStorageLimit
              : computeInitialStorageOverride(response);

          return buildStorageStatsFromImpact(response, resolvedStorageLimit);
        });
      })
      .catch(() => {
        setSourceStorageByOrgId({});
      });
  }, [
    getAffectedUsers,
    getStorageImpact,
    hasSameTopologyAsMergerDetails,
    hasInitialAffectedUsers,
    hasInitialOriginalRoleData,
    hasUsableInitialAffectedUsers,
    initialAffectedUsersResponse,
    initialStorageImpactResponse,
    sourceIds,
    survivorId,
  ]);

  useEffect(() => {
    if (!affectedUsersResponseForCommunication) {
      return;
    }

    const defaultRecipientByOrg: Record<number, string> = {};
    [
      affectedUsersResponseForCommunication.target,
      ...affectedUsersResponseForCommunication.sources,
    ].forEach((group) => {
      const orgId = Number(group.org_id);
      const adminUsers = [...group.users]
        .filter((user) => {
          const normalizedRole =
            typeof user.original_user_role === 'string'
              ? user.original_user_role.trim().toUpperCase()
              : typeof user.role === 'string'
              ? user.role.trim().toUpperCase()
              : typeof user.merged_user_role === 'string'
              ? user.merged_user_role.trim().toUpperCase()
              : '';
          return ['ADMIN', 'SUPER_ADMIN'].includes(normalizedRole);
        })
        .sort((first, second) => first.id - second.id);

      const defaultEmail = adminUsers
        .map((user) =>
          typeof user.email === 'string' ? user.email.trim() : ''
        )
        .find((email) => email.length > 0);

      if (defaultEmail) {
        defaultRecipientByOrg[orgId] = defaultEmail;
      }
    });

    if (Object.keys(defaultRecipientByOrg).length === 0) {
      return;
    }

    // Merges computed default recipients (derived from the async
    // affectedUsersResponseForCommunication data) into the sticky
    // selectedRecipientByOrgId state, once that data resolves. This only
    // fills in orgs without an existing selection, so it's an external-data
    // sync rather than something derivable purely from render.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedRecipientByOrgId((prev) => {
      const next = { ...savedRecipientsByOrgId, ...prev };
      Object.entries(defaultRecipientByOrg).forEach(([orgId, email]) => {
        const numericOrgId = Number(orgId);
        if (!next[numericOrgId]) {
          next[numericOrgId] = email;
        }
      });
      return next;
    });
  }, [affectedUsersResponseForCommunication, savedRecipientsByOrgId]);

  const handleEmailRecipientSelect = useCallback(
    (orgId: number, email: string) => {
      setSelectedRecipientByOrgId((prev) => ({
        ...prev,
        [orgId]: email,
      }));
    },
    []
  );

  const handleStorageStatsDraftChange = useCallback((stats: StorageStats) => {
    storageStatsDraftRef.current = stats;
  }, []);

  const getCommittedStorageStats = useCallback((): StorageStats => {
    const draftStats = storageStatsDraftRef.current;
    if (draftStats) {
      setStorageStats(draftStats);
      return draftStats;
    }
    return storageStats;
  }, [storageStats]);

  const stepContent = (() => {
    if (currentStep === 0) {
      return (
        <TopologyStep
          orgOptions={topologyOrgOptions}
          survivorId={survivorId}
          sourceIds={sourceIds}
          topologyError={topologyError}
          isReadOnly={isReadOnly}
          onSurvivorChange={setSurvivorId}
          onSourcesChange={setSourceIds}
        />
      );
    }

    if (currentStep === 1 && survivorId !== null) {
      return (
        <UsersStep
          initialAffectedUsersResponse={affectedUsersResponseForCommunication}
          isLoading={isLoadingAffectedUsers}
        />
      );
    }

    if (currentStep === 2 && survivorId !== null) {
      return (
        <StorageStep
          survivorId={survivorId}
          sourceIds={sourceIds}
          initialNewStorageLimit={Number(storageStats.newStorageLimit ?? 0)}
          initialStorageImpactResponse={initialStorageImpactResponse}
          shouldUseInitialImpact={hasSameTopologyAsMergerDetails}
          isReadOnly={isReadOnly}
          onStorageStatsDraftChange={handleStorageStatsDraftChange}
        />
      );
    }

    if (!survivor) {
      return null;
    }

    return (
      <CommunicationStep
        survivor={survivor}
        sources={sources}
        storageStats={storageStats}
        initialAffectedUsersResponse={affectedUsersResponseForCommunication}
        selectedRecipientKey={selectedRecipientKey}
        onRecipientSelect={setSelectedRecipientKey}
        selectedRecipientByOrgId={selectedRecipientByOrgId}
        onEmailRecipientSelect={handleEmailRecipientSelect}
        isReadOnly={isReadOnly}
        sourceUserCountsByOrgId={sourceUserCountsByOrgId}
        sourceStorageByOrgId={sourceStorageByOrgId}
        targetApproval={mergerDetails?.target_approval ?? null}
        sourceApprovals={mergerDetails?.source_approvals ?? null}
      />
    );
  })();

  const handleSaveDraft = async () => {
    const committedStorageStats = getCommittedStorageStats();
    const preparerId = appUser?.id ?? mergerDetails?.preparer_id;

    if (survivorId === null || sourceIds.length === 0 || !preparerId) {
      showToast({
        title: 'Missing merge request data.',
        description: 'Please verify survivor/source orgs and try again.',
        status: 'error',
      });
      return;
    }

    const payload = {
      target_org: survivorId,
      source_orgs: sourceIds.join(','),
      preparer_id: preparerId,
      status: 'DRAFT' as const,
      new_storage_limit: Number(committedStorageStats.newStorageLimit ?? 0),
      target_recipient: selectedRecipientByOrgId[survivorId],
      source_recipients: sourceIds
        .filter((sourceOrgId) => Boolean(selectedRecipientByOrgId[sourceOrgId]))
        .map((sourceOrgId) => ({
          org_id: sourceOrgId,
          email: selectedRecipientByOrgId[sourceOrgId],
        })),
    };

    try {
      if (mergerDetails?.merger_id) {
        await updateMerger({
          ...payload,
          merger_id: mergerDetails.merger_id,
        });
      } else {
        await createMerger(payload);
      }

      showToast({
        title: 'Merge request draft saved.',
        description: 'Your draft changes have been stored.',
        status: 'success',
      });
      await onMergerSaved?.();
    } catch (err) {
      console.error('failed to save merger draft: ', err);
      showToast({
        title: 'Failed to save merge request draft.',
        description: getErrorDescription(err, 'Please try again.'),
        status: 'error',
      });
    }
  };

  const handleNext = () => {
    if (!isReadOnly && currentStep === 2) {
      const committedStorageStats = getCommittedStorageStats();
      if (committedStorageStats.newStorageLimit <= 0) {
        return;
      }
    }

    if (currentStep < STEP_LABELS.length - 1) {
      setCurrentStep((step) => step + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep((step) => step - 1);
    }
  };

  const handleCreateMergeRequest = async () => {
    const preparerId = appUser?.id ?? mergerDetails?.preparer_id;

    if (survivorId === null || sourceIds.length === 0 || !preparerId) {
      showToast({
        title: 'Missing merge request data.',
        description: 'Please verify survivor/source orgs and try again.',
        status: 'error',
      });
      return;
    }

    const missingRecipientOrgIds = [survivorId, ...sourceIds].filter(
      (orgId) => !selectedRecipientByOrgId[orgId]
    );

    if (missingRecipientOrgIds.length > 0) {
      showToast({
        title: 'Missing recipient email(s).',
        description:
          'Please select recipient emails for the survivor and all source organizations before submitting.',
        status: 'error',
      });
      return;
    }

    const requestPayload = {
      target_org: survivorId,
      source_orgs: sourceIds.join(','),
      preparer_id: preparerId,
      new_storage_limit: Number(storageStats.newStorageLimit ?? 0),
      status: 'PENDING' as const,
      target_recipient: selectedRecipientByOrgId[survivorId],
      source_recipients: sourceIds.map((sourceOrgId) => ({
        org_id: sourceOrgId,
        email: selectedRecipientByOrgId[sourceOrgId],
      })),
    };

    try {
      if (mergerDetails?.merger_id) {
        await updateMerger({
          ...requestPayload,
          merger_id: mergerDetails.merger_id,
        });
      } else {
        await createMerger(requestPayload);
      }

      showToast({
        title:
          mode === 'edit' ? 'Merge request updated.' : 'Merge request created.',
        description:
          mode === 'edit'
            ? 'Your merge request changes have been saved.'
            : 'Your merge request has been submitted.',
        status: 'success',
      });
      await onMergerSaved?.();
      onClose();
    } catch (err) {
      console.error('failed to submit merger request: ', err);
      showToast({
        title: 'Failed to submit merge request.',
        description: getErrorDescription(err, 'Please try again.'),
        status: 'error',
      });
    }
  };

  const performExecuteMergeRequest = async () => {
    if (!mergerDetails?.merger_id) {
      showToast({
        title: 'Missing merge request data.',
        description: 'Please refresh and try again.',
        status: 'error',
      });
      return;
    }

    try {
      await executeMerger(mergerDetails.merger_id);
      showToast({
        title: 'Merge request started.',
        description: 'The merge request is executing.',
        status: 'success',
      });
      await onMergerSaved?.();
      onClose();
    } catch (err) {
      console.error('failed to merge request: ', err);
      showToast({
        title: 'Failed to update merge request.',
        description: getErrorDescription(err, 'Please try again.'),
        status: 'error',
      });
    }
  };

  const handleExecuteMergeRequest = () => {
    openConfirmDialog({
      title: 'Execute merge request',
      body: 'This merge action is irreversible. Once started, source organizations will be merged and deactivated. Are you sure you want to continue?',
      confirmLabel: 'Merge request',
      cancelLabel: 'Cancel',
      isDestructive: true,
      onConfirm: performExecuteMergeRequest,
    });
  };

  return (
    <Box
      width="min(960px, 92vw)"
      display="grid"
      gridTemplateRows="auto auto 1fr auto"
      h="100%"
      overflowY="hidden"
      rowGap={4}
      data-testid="merge-create-wizard"
    >
      <Flex alignItems="center" justifyContent="space-between" gap={3}>
        <Button
          colorScheme="gray"
          variant="ghost"
          onClick={onClose}
          rounded={4}
          data-testid="merge-back-to-list"
          leftIcon="arrowLeft"
        >
          Back to List
        </Button>
        {mode !== 'view' ? (
          <Button
            colorScheme="gray"
            rounded={4}
            onClick={handleSaveDraft}
            data-testid="merge-save-draft"
          >
            Save Draft
          </Button>
        ) : (
          <Box
            px={3}
            py={2}
            borderRadius={6}
            bg={viewModeIndicatorStyles.bg}
            color={viewModeIndicatorStyles.color}
            fontSize={12}
            fontWeight={700}
            letterSpacing="0.04em"
            textTransform="uppercase"
            border="1px solid"
            borderColor={viewModeIndicatorStyles.borderColor}
            data-testid="merge-read-only-indicator"
          >
            {viewModeIndicatorLabel}
          </Box>
        )}
      </Flex>

      <StepperHeader currentStep={currentStep} />

      <Box
        border="1px solid"
        borderColor="whiteAlpha.300"
        borderRadius={10}
        p={6}
        overflow="auto"
        bg="blackAlpha.300"
      >
        {stepContent}
      </Box>

      <Flex
        pt={4}
        justifyContent={currentStep === 0 ? 'flex-end' : 'space-between'}
      >
        {currentStep > 0 ? (
          <Button
            colorScheme="gray"
            rounded={4}
            type="button"
            onClick={handlePrevious}
            data-testid="merge-previous-step"
          >
            Previous Step
          </Button>
        ) : null}

        {isFinalStep ? (
          shouldShowFinalActionButton ? (
            <Button
              type="button"
              onClick={
                isPendingMerger
                  ? handleExecuteMergeRequest
                  : handleCreateMergeRequest
              }
              data-testid="merge-create-request"
              rounded={4}
              disabled={isFinalActionDisabled}
            >
              {finalActionLabel}
            </Button>
          ) : null
        ) : (
          <Button
            type="button"
            rounded={4}
            onClick={handleNext}
            disabled={isNextDisabled}
            data-testid="merge-next-step"
          >
            Next Step
          </Button>
        )}
      </Flex>
    </Box>
  );
}
